<template>
  <!-- eslint-disable prefer-standard-components/no-disabled-on-standard-button-->
  <v-dialog
    v-model="isVisible"
    @click:outside="maybeClose"
    @keydown.esc="maybeClose"
    :fullscreen="fullscreen"
    :max-width="fullscreen ? null : maxWidth"
    :scrim="scrim || undefined"
    persistent
  >
    <v-card
      :data-testid="contentTestId || 'resource-dialog'"
      data-cy="resource-dialog"
      border
      tile
    >
      <v-card-title
        v-if="titleOverride || title || $slots.header"
        :class="titleClass"
      >
        <v-row
          class="d-flex align-center justify-start"
          dense
        >
          <slot name="header" />

          <template v-if="!$slots.header">
            <v-col
              :cols="$slots.titleActions || $slots.close || closeable ? 10 : 12"
              class="wrap-title"
              tabindex="0"
            >
              <div
                class="wrap-text"
                data-cy="resource-dialog-title"
                data-testid="resource-dialog-title"
              >
                {{ $bct(titleOverride || title) }}
              </div>
            </v-col>

            <v-col
              v-if="$slots.titleActions || $slots.close || closeable"
              class="d-flex justify-end"
              cols="2"
              tabindex="0"
            >
              <slot name="title-actions" />

              <slot name="close">
                <StandardButton
                  v-if="closeable"
                  @click="close"
                  :disabled="processing"
                  aria-label="Close dialog"
                  color="black"
                  icon="close"
                  role="link"
                />
              </slot>
            </v-col>
          </template>
        </v-row>
      </v-card-title>

      <v-card-text v-if="subtitle">
        <v-row
          class="d-flex align-center justify-start"
          dense
        >
          <v-col
            cols="12"
            tabindex="0"
          >
            <div class="fs-16 text-wrap">
              {{ $bct(subtitle) }}
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-text :class="textClass || defaultTextClass">
        <slot name="description" />

        <slot name="prepend" />

        <slot
          :local-value="localValue"
          name="form"
        >
          <ResourceForm
            v-if="fields.length > 0"
            v-model="localValue"
            @change="emit('change', $event)"
            @search="emit('search', $event)"
            :fields="fields"
          />
        </slot>

        <slot
          :local-value="localValue"
          name="append"
        />

        <p
          v-if="consentText"
          class="c-light-black fs-16 mt-6"
        >
          {{ $bct(consentText) }}
        </p>

        <v-checkbox
          v-if="consentLabel"
          v-model="consent"
          :label="consentLabel"
        />
      </v-card-text>

      <template v-if="!hideActions">
        <v-divider />

        <v-card-actions
          :class="[
            $vuetify.display.smAndDown
              ? 'd-flex flex-column mb-2'
              : 'd-flex flex-row justify-space-between',
            'mt-0',
            'px-4',
            'py-4',
          ]"
        >
          <slot name="actions">
            <StandardButton
              v-if="cancellable"
              @click="cancel"
              :custom-classes="[
                $vuetify.display.smAndDown ? 'btn-small' : 'btn-large',
                $vuetify.display.smAndDown ? 'mb-4 me-0' : 'me-3',
                'focus-very-visible',
                'd-flex',
              ]"
              :full-width="$vuetify.display.smAndDown"
              :loading="processing"
              :prepend-icon="cancelButtonIcon"
              :text="cancelButtonText"
              color="primary"
              variant="outlined"
            />

            <div :class="$vuetify.display.mdAndUp ? 'flex-row d-flex' : 'w-100'">
              <StandardButton
                v-if="draftable"
                @click="saveDraft"
                :custom-classes="[
                  $vuetify.display.smAndDown ? 'btn-small' : 'btn-large',
                  $vuetify.display.smAndDown ? 'mb-4 ms-0 me-0' : 'me-3',
                  'focus-very-visible',
                  'd-flex',
                ]"
                :full-width="$vuetify.display.smAndDown"
                :loading="processing"
                :text="saveDraftButtonText"
                color="primary"
                data-testid="save-draft-button"
                variant="outlined"
              />

              <v-tooltip
                v-if="!hideSaveButton"
                :disabled="!saveButtonDisabledTooltip || !saveDisallowed"
                :text="saveButtonDisabledTooltip"
                location="top"
              >
                <template #activator="{ props: tooltipProps }">
                  <StandardButton
                    @click="save"
                    :bind-props="tooltipProps"
                    :color="saveButtonColor"
                    :custom-classes="[
                      $vuetify.display.smAndDown ? 'btn-small' : 'btn-large',
                      $vuetify.display.smAndDown ? 'ms-0 me-0' : 'me-2',
                      'focus-very-visible',
                      'd-flex',
                      saveBtnClasses,
                    ]"
                    :disabled="saveDisallowed"
                    :full-width="$vuetify.display.smAndDown"
                    :loading="processing"
                    :text="saveButtonText"
                    data-testid="save-button"
                  />
                </template>
              </v-tooltip>
            </div>
          </slot>

          <slot name="component-actions" />
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { useRoute } from 'vue-router';

import ResourceForm from '@/shared/components/form/resource/ResourceForm.vue';
import useRouterHelper from '@/shared/composables/useRouterHelper';

const route = useRoute();
const { updateQuery } = useRouterHelper();

const $bct = inject('translator');

const props = defineProps({
  action: {
    type: String,
    default: null,
  },
  cancelButtonIcon: {
    type: String,
    default: undefined,
  },
  cancelButtonText: {
    type: String,
    default: 'Cancel',
  },
  cancellable: {
    type: Boolean,
    default: true,
  },
  centerTitle: {
    type: Boolean,
    default: false,
  },
  closeOnSave: {
    type: Boolean,
    default: false,
  },
  closeable: {
    type: Boolean,
    default: false,
  },
  consentLabel: {
    type: String,
    default: null,
  },
  consentText: {
    type: String,
    default: null,
  },
  contentTestId: {
    type: String,
    default: null,
  },
  draftable: {
    type: Boolean,
    default: false,
  },
  fields: {
    type: Array,
    default() {
      return [];
    },
  },
  fullscreen: {
    type: Boolean,
    default: false,
  },
  hideActions: {
    type: Boolean,
    default: false,
  },
  maxWidth: {
    default: 400,
    type: Number,
  },
  processing: {
    type: Boolean,
    default: false,
  },
  saveButtonColor: {
    type: String,
    default: 'primary',
  },
  saveButtonDisabled: {
    type: Boolean,
    default: false,
  },
  saveButtonDisabledFunction: {
    type: Function,
    default: () => false,
  },
  saveButtonDisabledTooltip: {
    type: String,
    default: null,
  },
  saveButtonText: {
    type: String,
    default: 'Save',
  },
  saveDraftButtonText: {
    type: String,
    default: 'Save draft',
  },
  scrim: {
    type: Boolean,
    default: false,
  },
  subtitle: {
    type: String,
    default: null,
  },
  textClass: {
    type: String,
    default: null,
  },
  title: {
    type: String,
    default: null,
  },
  hideSaveButton: {
    type: Boolean,
    default: false,
  },
});

defineExpose({ clear, close, getLocalValue, setLocalValue, open });

const darkColors = ['red'];
const emit = defineEmits(['cancel', 'change', 'close', 'save', 'save:draft', 'search']);

const consent = ref(false);
const defaultTextClass = ref('pt-6 pb-6');
const isVisible = ref(false);
const localValue = ref({});
const titleOverride = ref(null);

watch(
  () => route?.query.action,
  async (newVal) => {
    if (!props.action) return;

    if (newVal && newVal === props.action) open();
    else if (isVisible.value) await close();
  },
  { immediate: true },
);

function requiredFieldValueMissing(value) {
  if (value === null || value === undefined) return true;
  if (value === '') return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  return false;
}

const missingRequiredFields = computed(() => {
  return !!(
    props.fields.some((field) => {
      const isRequired =
        typeof field.required === 'function' ? field.required(localValue.value) : !!field.required;
      if (isRequired && (!field.hidden || !field.hidden(localValue.value))) {
        return requiredFieldValueMissing(localValue.value[field.value]);
      }
      return false;
    }) ||
    (props.consentLabel && !consent.value)
  );
});

const saveBtnClasses = computed(() => {
  const displayWhiteText = darkColors.includes(props.saveButtonColor) && !saveDisallowed.value;
  if (displayWhiteText) return 'c-white';

  return null;
});

const saveDisallowed = computed(() => {
  return (
    props.saveButtonDisabled ||
    missingRequiredFields.value ||
    props.saveButtonDisabledFunction(localValue.value)
  );
});

const titleClass = computed(() => {
  const classes = [];
  if (props.centerTitle) classes.push('justify-center');
  return classes.join(' ');
});

const fieldKeysForSave = computed(() => {
  const keys = props.fields
    .map((field) => field.value)
    .filter((key) => key !== undefined && key !== null && key !== '');
  return keys.length > 0 ? keys : undefined;
});

async function cancel() {
  emit('cancel');
  await close();
}

function clear() {
  localValue.value = {};
}

async function close() {
  if (isVisible.value) {
    isVisible.value = false;
    emit('close');
    if (props.action) await updateQuery({ action: null });
  }
}

function getLocalValue() {
  return localValue.value;
}

async function maybeClose() {
  if (props.closeable && !props.processing) await close();
}

function open(val, title = null) {
  titleOverride.value = title;
  localValue.value = JSON.parse(JSON.stringify(val || {}));
  isVisible.value = true;
}

async function save() {
  if (saveDisallowed.value) return false;
  if (props.closeOnSave) await close();

  return emit('save', localValue.value, fieldKeysForSave.value);
}

function saveDraft() {
  return emit('save:draft', localValue.value, fieldKeysForSave.value);
}

function setLocalValue(newVal) {
  localValue.value = JSON.parse(JSON.stringify(newVal || {}));
}
</script>

<style scoped>
.btn-small {
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
}

.btn-large {
  font-size: 1.125rem;
  padding: 0.75rem 1.5rem;
}

.wrap-text {
  width: 100%;
}

.wrap-title {
  display: flex;
  text-align: left;
}

@media (max-width: 37.5rem) {
  .me-3 {
    margin-right: 0 !important;
  }
  .btn-small {
    padding: 0.2rem;
  }
}
</style>
